// src/lib/langPreference.ts
//
// Server-side language preference for signed-in users. The BFF keeps
// one row per account (issuer + sub); the browser pulls it once when
// the session resolves and pushes on every language change, so the
// choice follows the account across reloads, sign-outs and devices.
//
// Anonymous visitors never call this: their persistence is the
// localStorage copy handled by src/i18n/index.ts. Every failure here
// is silent on purpose — a preference sync must never break the app.

import i18n from "@/i18n";
import { bffCsrfHeaders } from "@/lib/bffAuth";

/** Apply the account's stored language (if any) to the UI. */
export async function pullLanguagePreference(): Promise<void> {
  try {
    const r = await fetch("/api/auth/preferences", {
      credentials: "same-origin",
    });
    if (!r.ok) return;
    const body = (await r.json()) as { language?: unknown };
    const language = typeof body?.language === "string" ? body.language : null;
    if (language && language !== i18n.language) {
      await i18n.changeLanguage(language);
    }
  } catch {
    // Offline or 5xx: keep the locally stored language.
  }
}

/** Persist the current language choice to the signed-in account. */
export async function pushLanguagePreference(language: string): Promise<void> {
  try {
    await fetch("/api/auth/preferences", {
      method: "PUT",
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
        ...bffCsrfHeaders(),
      },
      body: JSON.stringify({ language }),
    });
  } catch {
    // Network failure: the localStorage copy still holds the choice.
  }
}
